
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {Observable, Subject} from "rxjs";
import {CentreOption} from "../../../../../interfaces/Centres";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Employee} from "../../../../../interfaces/Employee";
import {InductionService} from "../../induction/induction.service";
import {ActivatedRoute, Router} from "@angular/router";
import {CentreService} from "../../../../../services/centre.service";
import {EmployeeService} from "../../../../../services/employee.service";
import {LogService} from "../../../../../services/log.service";
import {EducationMom} from "../education-mom.model";
import {EducationMomService} from "../education-mom.service";
import firebase from "firebase/compat/app";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {AngularFireDatabase} from "@angular/fire/compat/database";
import {MatSelectChange} from "@angular/material/select";
import Editor from "@ckeditor/ckeditor5-build-classic";
import {AngularFireStorage} from "@angular/fire/compat/storage";
import {map, takeUntil} from "rxjs/operators";
import {AddParticipantsDialogComponent} from "../add-participants-dialog/add-participants-dialog.component";

// ✅ Custom Upload Adapter for Firebase Storage
class FirebaseUploadAdapter {
  loader: any;
  storage: AngularFireStorage;

  constructor(loader: any, storage: AngularFireStorage) {
    this.loader = loader;
    this.storage = storage;
  }

  upload() {
    return this.loader.file.then((file: File) => new Promise((resolve, reject) => {
      const filePath = `education-mom-images/${Date.now()}_${file.name}`;
      const fileRef = this.storage.ref(filePath);
      const uploadTask = this.storage.upload(filePath, file);

      uploadTask.percentageChanges().subscribe(
        (progress) => {
          console.log(`Upload progress: ${progress}%`);
        }
      );

      uploadTask.snapshotChanges().subscribe(
        () => {},
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        () => {
          fileRef.getDownloadURL().subscribe((url) => {
            resolve({ default: url });
          });
        }
      );
    }));
  }

  abort() {
    console.log('Upload aborted');
  }
}

@Component({
  selector: 'app-add-edit-education-mom',
  templateUrl: './add-edit-education-mom.component.html',
  styleUrls: ['./add-edit-education-mom.component.scss']
})
export class AddEditEducationMomComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();
  allCenters$!: Observable<CentreOption[]>;
  displayCentre = (c: any) => c?.centreName;

  sessionDisplay = (value: string) => value;
  subjects: string[] = ['Language', 'Math', 'EVS & GK'];
  subjectsDisplay = (value: string) => value;
  momForm !: FormGroup;
  observerName: any
  allTeachers: any[] = []
  filteredTeachers: any[] = []
  participantSearch: string = ''
  employeeData!: Employee[];
  contentLength = 0;
  currentEditId: any;
  isLoading: boolean = false;
  selectedCentre: any;
  typeOfMeeting$: Observable<any>;
  type: string[] = ['Online', 'Offline', 'Telephonic','Others'];

  // ✅ CKEditor Configuration
  public Editor = Editor;
  public editorConfig = {
    toolbar: [
      'heading', '|',
      'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
      'imageUpload', 'blockQuote', 'insertTable', 'mediaEmbed', '|',
      'undo', 'redo'
    ],
    image: {
      toolbar: [
        'imageTextAlternative', 'imageStyle:full', 'imageStyle:side'
      ]
    }
  };

  constructor(private fb: FormBuilder,
              private educationMomService: EducationMomService,
              private router: Router,
              private route: ActivatedRoute,
              private centreService: CentreService,
              private userService: EmployeeService,
              private mLogService: LogService,
              private mDatabase: AngularFireDatabase,
              private storage: AngularFireStorage, // ✅ Inject Firebase Storage
              private dialog: MatDialog, // ✅ Inject MatDialog
              @Inject(MAT_DIALOG_DATA) public dialogData: any,
              private dialogRef: MatDialogRef<AddEditEducationMomComponent>) {
    this.allCenters$ = this.centreService.getAllCentres().pipe(
      map(centres => [{centreName:'Head Office',centreCode:'Ho'},...centres]))


    this.typeOfMeeting$ = this.mDatabase.list('types/Education Type Of Meeting').valueChanges();
  }

  ngOnInit(): void {
    this.formInit();

    // Load default teachers on init (all Teachers by default)
    this.loadTeachersByRole(['Teacher']);

    //  Detect if opened inside modal
    if (this.dialogData && this.dialogData.id) {
      this.currentEditId = this.dialogData.id;
      this.patchEditDataFromDialog();
    }
    // If opened through route (old method)
    else {
      this.currentEditId = this.route.snapshot.paramMap.get('id');
      if (this.currentEditId) {
        this.patchEditData();
      }
    }
  }

  // ✅ CKEditor Ready Event - Attach Upload Adapter
  onEditorReady(editor: any) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => {
      return new FirebaseUploadAdapter(loader, this.storage);
    };
  }

  patchEditDataFromDialog() {
    const record = this.dialogData;

    if (record) {
      // Load appropriate teachers based on type before patching
      if (record.type) {
        this.loadTeachersForType(record.type, () => {
          const matchedParticipants = this.matchParticipants(record.participants || []);

          this.momForm.patchValue({
            date: record.date?.toDate ? record.date.toDate() : record.date,
            participants: matchedParticipants,
            centreName: record.centreName,
            centreCode: record.centreCode,
            type: record.type,
            place: record.place,
            content: record.content,
            suggestions: record.suggestions || ''
          });

          this.selectedCentre = {
            centreName: record.centreName,
            centreCode: record.centreCode
          };
        });
      }
    }
  }

  compareParticipants = (o1: any, o2: any): boolean => {
    if (!o1 || !o2) return false;
    return o1.name === o2.name;
  }

  matchParticipants(savedParticipants: any[]): any[] {
    if (!savedParticipants || !this.allTeachers.length) return [];

    return savedParticipants.map(savedP => this.allTeachers.find(teacher => teacher.name === savedP.name))
      .filter(Boolean);
  }

  formInit() {
    this.momForm = this.fb.group({
      date: ['', Validators.required],
      participants: [[], Validators.required],
      centreName: ['', Validators.required],
      centreCode: ['', Validators.required],
      type: ['', Validators.required],
      place: ['', Validators.required],
      content: ['', Validators.required],
      suggestions: ['']
    });
  }


  submitForm() {
    if (this.momForm.invalid) return;

    this.isLoading = true;

    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const username = `${userData.userName || ''}`.trim() || 'Unknown User';

    const payload: EducationMom = {
      ...this.momForm.value,
      date: new Date(this.momForm.value.date)
    };

    if (this.currentEditId) {
      payload.updatedBy = username;
      payload.updatedAt = firebase.firestore.FieldValue.serverTimestamp();

      this.educationMomService.update(this.currentEditId, payload)
        .then(() => {
          this.mLogService.addLog({
            date: Date.now(),
            section: 'Education Meeting Induction',
            action: 'Updated',
            description: `${username} updated the Education Meeting`,
            currentIp: localStorage.getItem('currentip')!
          });
          this.dialogRef.close();
          this.afterSave('Updated successfully!');
        });
    } else {
      payload.createdBy = username;
      payload.createdAt = firebase.firestore.FieldValue.serverTimestamp();
      payload.status = "Active";

      this.educationMomService.create(payload)
        .then(() => {
          this.mLogService.addLog({
            date: Date.now(),
            section: 'Education Meeting',
            action: 'Created',
            description: `${username} created a new Education Meeting`,
            currentIp: localStorage.getItem('currentip')!
          });
          this.dialogRef.close();
          this.afterSave('Saved successfully!');
        });
    }
  }

  private afterSave(message: string) {
    this.isLoading = false;
    alert(message);
    this.router.navigate(['/home/education/education-mom-list']);
    this.momForm.reset();
    this.currentEditId = null;
  }

  fetchLeadTeacher() {
    this.userService.getUsers().pipe(takeUntil(this._destroyed$)).subscribe(snapshotchanges => {
      this.employeeData = snapshotchanges.map(w => {
        let emp = w.payload.toJSON() as Employee;
        if (w.key != null) {
          emp['id'] = w.key;
        }
        return emp;
      });
      const namesToRemove = ['Shilpa Pahuja', 'Mukti Panchamia'];
      this.observerName = this.employeeData
        .filter(employee => employee?.jobtitle === 'Lead Teacher' && employee.disabled !== true)
        .map(counsellingEmployee => ({name: counsellingEmployee?.userName}))
        .filter(c => !namesToRemove.includes(c?.name))
        .sort((a, b) => a?.name.localeCompare(b?.name));
    });
  }

  patchEditData() {
    this.educationMomService.getDataById(this.currentEditId).pipe(takeUntil(this._destroyed$)).subscribe((record: any) => {
      if (record) {
        if (record.type) {
          this.loadTeachersForType(record.type, () => {
            const matchedParticipants = this.matchParticipants(record.participants);

            this.momForm.patchValue({
              date: record.date.toDate(),
              participants: matchedParticipants,
              centreName: record.centreName,
              centreCode: record.centreCode,
              type: record.type,
              place: record.place,
              content: record.content,
              suggestions: record?.suggestions,
            });
            this.selectedCentre = {centreName: record.centreName, centreCode: record.centreCode};
          });
        }
      }
    });
  }

  onParticipantSearchChange(event: Event): void {
    const searchValue = (event.target as HTMLInputElement).value;
    this.participantSearch = searchValue;
    this.filterParticipants();
  }

  filterParticipants(): void {
    const searchValue = this.participantSearch.toLowerCase().trim();

    if (!searchValue) {
      this.filteredTeachers = [...this.allTeachers];
      return;
    }

    // Only show items that match the search
    this.filteredTeachers = this.allTeachers.filter((t: any) =>
      t.name.toLowerCase().includes(searchValue)
    );
  }

  onParticipantDropdownOpened(opened: boolean) {
    if (opened) {
      this.participantSearch = '';
      this.filteredTeachers = [...this.allTeachers];
    }
  }

  resetForm() {
    this.momForm.reset();
    this.currentEditId = null;
    this.participantSearch = '';
    this.filteredTeachers = [...this.allTeachers];
  }

  onCancel() {
    this.dialogRef.close();
  }

  get suggestionsLength(): number {
    const val: string = this.momForm.get('suggestions')?.value || '';
    return val.length;
  }

  // onCentreSelect($event: any) {
  //   console.log($event)
  //   this.momForm.patchValue({
  //     centreName: $event.centreName,
  //     centreCode: $event.centreCode
  //   })
  // }

  onCentreSelect($event: any) {
    console.log($event);

    // Check if the selection is valid
    if ($event && $event.centreName && $event.centreCode) {
      this.momForm.patchValue({
        centreName: $event.centreName,
        centreCode: $event.centreCode
      });

      // Clear any previous errors
      this.momForm.get('centreName')?.setErrors(null);
      this.momForm.get('centreCode')?.setErrors(null);
    } else {
      // If invalid selection, clear the values and set error
      this.momForm.patchValue({
        centreName: '',
        centreCode: ''
      });

      this.momForm.get('centreName')?.setErrors({ invalidSelection: true });
      this.momForm.get('centreCode')?.setErrors({ invalidSelection: true });
    }
  }

  onTypeChange(event: MatSelectChange) {
    const type = event.value;
    this.momForm.patchValue({participants: []});
    this.loadTeachersForType(type);
  }

  loadTeachersForType(type: string, callback?: () => void) {
    if (type === 'Weekly Teachers Meeting') {
      this.loadTeachersByRole(['Teacher', 'Lead Teacher', 'Sr Teacher'], [], callback);
    } else if (type === 'Meeting with Kavita Mam') {
      this.loadTeachersByRole([], ['ldteacher@accesslife.org', 'mgrops.mumbai@accesslife.org','mgrops@accesslife.org', 'ldcounsellor@accesslife.org', 'poc@accesslife.org'], callback);
    } else if (type === 'Meeting with Management') {
      this.loadTeachersByRole([], ['ldteacher@accesslife.org', 'mgrops@accesslife.org', 'genmgr@accesslife.org', 'ldcounsellor@accesslife.org', 'srteacher@accesslife.org', 'kala@accesslife.org', 'girish@accesslife.org', 'poc@accesslife.org', 'mgrops.mumbai@accesslife.org', 'gmdev@accesslife.org'], callback);
    } else {
      this.loadTeachersByRole(['Teacher'], [], callback);
    }
  }

  loadTeachersByRole(roles: string[] = [], emails: string[] = [], callback?: () => void) {
    const namesToRemove = ['Shilpa Pahuja', 'Mukti Panchamia'];
    this.userService.getUsers().pipe(takeUntil(this._destroyed$)).subscribe(snapshotchanges => {
      this.employeeData = snapshotchanges.map(w => {
        let emp = w.payload.toJSON() as Employee;
        if (w.key) emp['id'] = w.key;
        return emp;
      });
      let teachers = this.employeeData;

      if (roles.length > 0) {
        teachers = teachers.filter(emp => roles.includes(emp.role));
      }
      if (emails.length > 0) {
        teachers = teachers.filter(emp => emails.includes(emp.email));
      }
      teachers = teachers.filter(emp =>
        !namesToRemove.includes(emp.userName) && emp.disabled !== true
      );

      this.allTeachers = teachers.map(emp => ({
        name: emp.userName,
        email: emp.email
      })).sort((a, b) => a.name.localeCompare(b.name));
      this.filteredTeachers = [...this.allTeachers];

      if (callback) {
        callback();
      }
    });
  }

  openParticipantsDialog(): void {
    const currentParticipants = this.momForm.get('participants')?.value || [];

    const dialogRef = this.dialog.open(AddParticipantsDialogComponent, {
      width: '700px',
      maxHeight: '90vh',
      data: { currentParticipants }
    });

    dialogRef.afterClosed().pipe(takeUntil(this._destroyed$)).subscribe(result => {
      if (result && result.length > 0) {
        // Merge selected participants with existing allTeachers array
        // This ensures that participants from dialog (all employees) are available in the dropdown
        const existingEmails = new Set(this.allTeachers.map(t => t.email));
        const newParticipants = result.filter((p: any) => !existingEmails.has(p.email));

        // Add new participants to allTeachers
        this.allTeachers = [...this.allTeachers, ...newParticipants].sort((a, b) => a.name.localeCompare(b.name));

        // Update filtered teachers
        this.filteredTeachers = [...this.allTeachers];

        // Update form control with selected participants
        this.momForm.patchValue({ participants: result });
      }
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}

